/**
 * Collection Grid Web Component
 *
 * Loads all products from a collection, sorts them by priority tags,
 * and displays them with infinite scroll.
 */
class CollectionGrid extends HTMLElement {
  constructor() {
    super();
    this.allProducts = [];       // Sorted array of product HTML elements
    this.displayedCount = 0;
    this.isLoading = false;
    this.scrollObserver = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-collection-grid]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.gridContainer = this.querySelector('[data-grid-container]');
    this.sentinel = this.querySelector('[data-scroll-sentinel]');
    this.loadingMore = this.querySelector('[data-loading-more]');
    this.emptyState = this.querySelector('[data-empty-state]');

    this.collectionHandle = this.dataset.collectionHandle;
    this.sectionId = this.dataset.sectionId;
    this.productsPerLoad = parseInt(this.dataset.productsPerLoad, 10) || 24;
    this.priorityTags = this.dataset.priorityTags
      ? this.dataset.priorityTags.split(',').map(t => t.trim().toLowerCase())
      : [];
    this.totalProducts = parseInt(this.dataset.totalProducts, 10) || 0;

    // Don't initialize if no collection selected
    if (!this.collectionHandle) {
      this.loadingState.style.display = 'none';
      this.emptyState.style.display = '';
      return;
    }

    // Load products on first scroll or after browser idle
    this.initLazyLoad();
  }

  initLazyLoad() {
    // Load products on first scroll anywhere on the page
    // This ensures products are ready by the time user scrolls to the section
    const onFirstScroll = () => {
      if (!this.initialized) {
        this.initialized = true;
        window.removeEventListener('scroll', onFirstScroll);
        this.loadAllProducts();
      }
    };

    window.addEventListener('scroll', onFirstScroll, { passive: true });

    // Also load after a short idle period if user doesn't scroll
    // This handles cases where the grid might be visible without scrolling
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => {
        if (!this.initialized) {
          this.initialized = true;
          window.removeEventListener('scroll', onFirstScroll);
          this.loadAllProducts();
        }
      }, { timeout: 2000 });
    } else {
      // Fallback for Safari
      setTimeout(() => {
        if (!this.initialized) {
          this.initialized = true;
          window.removeEventListener('scroll', onFirstScroll);
          this.loadAllProducts();
        }
      }, 1000);
    }
  }

  disconnectedCallback() {
    if (this.scrollObserver) this.scrollObserver.disconnect();
  }

  getCacheKey() {
    return `collection-grid-${window.location.pathname}-${this.collectionHandle}`;
  }

  getCachedProducts() {
    // sessionStorage access can throw in iOS Safari Private Browsing / when
    // storage is disabled, so the whole read is guarded.
    try {
      const cacheKey = this.getCacheKey();
      const cached = sessionStorage.getItem(cacheKey);
      if (!cached) return null;

      const { html, timestamp } = JSON.parse(cached);
      const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
      if (Date.now() - timestamp > CACHE_EXPIRY) {
        sessionStorage.removeItem(cacheKey);
        return null;
      }
      return html; // Array of outerHTML strings
    } catch (e) {
      return null;
    }
  }

  cacheProducts(products) {
    const cacheKey = this.getCacheKey();
    const html = products.map(el => el.outerHTML);
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({
        html,
        timestamp: Date.now()
      }));
    } catch (e) {
      // sessionStorage may be full or unavailable
      console.log('Collection Grid: Could not cache products', e);
    }
  }

  loadFromCache(htmlArray) {
    const parser = new DOMParser();
    this.allProducts = htmlArray.map(html => {
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.firstChild;
    });

    // Hide loading, show grid
    this.loadingState.style.display = 'none';
    this.gridContainer.style.display = '';

    // Display first batch
    this.displayNextBatch();

    // Setup infinite scroll if needed
    if (this.displayedCount < this.allProducts.length) {
      this.sentinel.style.display = '';
      this.initInfiniteScroll();
    }
  }

  async loadAllProducts() {
    // Check cache first
    const cachedHTML = this.getCachedProducts();
    if (cachedHTML) {
      console.log('Collection Grid: Loading from cache');
      this.loadFromCache(cachedHTML);
      return;
    }

    const allProductElements = [];
    let page = 1;
    let hasMore = true;
    let loadFailed = false;

    console.log('Collection Grid: Starting load for', this.collectionHandle);
    console.log('Collection Grid: Priority tags:', this.priorityTags);

    while (hasMore) {
      const url = `/collections/${this.collectionHandle}?page=${page}&section_id=${this.sectionId}`;

      try {
        // no-store: Safari caches aggressively and can serve a response that
        // lacks the [data-product-source] wrapper, which looks like 0 products.
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract product blocks from the hidden source container in the response
        const sourceContainer = doc.querySelector('[data-product-source]');
        if (!sourceContainer) {
          console.log('Collection Grid: No source container found on page', page);
          hasMore = false;
          break;
        }

        const products = sourceContainer.querySelectorAll('[data-product-block]');
        const hasNextPage = sourceContainer.dataset.hasNextPage === 'true';
        console.log('Collection Grid: Page', page, 'found', products.length, 'products, hasNextPage:', hasNextPage);

        if (products.length === 0) {
          hasMore = false;
        } else {
          products.forEach((product) => {
            allProductElements.push({
              element: product.cloneNode(true),
              tags: this.extractTags(product),
              id: product.dataset.productId
            });
          });

          // Use Shopify's pagination info to determine if there are more pages
          if (hasNextPage) {
            page++;
          } else {
            hasMore = false;
          }
        }

        // Safety limit to prevent infinite loops
        if (page > 100) hasMore = false;

      } catch (error) {
        console.error('Collection Grid: Error loading products:', error);
        loadFailed = true;
        hasMore = false;
      }
    }

    console.log('Collection Grid: Total products loaded:', allProductElements.length);

    // A fetch failure must not be silently shown as "no products". If we got
    // nothing because a request errored, surface a retry instead of the empty
    // state — otherwise a transient/Safari fetch issue masquerades as an empty
    // collection.
    if (allProductElements.length === 0 && loadFailed) {
      this.showError();
      return;
    }

    // Handle genuinely empty collection
    if (allProductElements.length === 0) {
      this.loadingState.style.display = 'none';
      this.emptyState.style.display = '';
      return;
    }

    // Sort: priority products first, then regular
    this.allProducts = this.sortProducts(allProductElements);

    // Cache the sorted products for back-button navigation
    this.cacheProducts(this.allProducts);

    // Hide loading, show grid
    this.loadingState.style.display = 'none';
    this.gridContainer.style.display = '';

    // Display first batch
    this.displayNextBatch();

    // Setup infinite scroll if there are more products to show
    if (this.displayedCount < this.allProducts.length) {
      this.sentinel.style.display = '';
      this.initInfiniteScroll();
    }
  }

  showError() {
    this.loadingState.style.display = 'none';
    this.emptyState.style.display = '';
    this.emptyState.innerHTML =
      '<p>Sorry, products couldn’t be loaded.</p>' +
      '<button type="button" class="btn btn--secondary" data-retry-load>Try again</button>';
    const retry = this.emptyState.querySelector('[data-retry-load]');
    if (retry) {
      retry.addEventListener('click', () => {
        this.emptyState.style.display = 'none';
        this.loadingState.style.display = '';
        this.loadAllProducts();
      }, { once: true });
    }
  }

  extractTags(productElement) {
    // Tags stored in data attribute on product-block
    const tagsAttr = productElement.dataset.productTags || '';
    return tagsAttr
      .split(',')
      .map(t => t.trim().toLowerCase())
      .filter(t => t !== '');
  }

  sortProducts(products) {
    // If no priority tags configured, return original order
    if (this.priorityTags.length === 0) {
      return products.map(p => p.element);
    }

    const priority = [];
    const regular = [];

    products.forEach((product) => {
      const hasPriorityTag = product.tags.some(tag =>
        this.priorityTags.includes(tag)
      );

      if (hasPriorityTag) {
        priority.push(product.element);
      } else {
        regular.push(product.element);
      }
    });

    return [...priority, ...regular];
  }

  displayNextBatch() {
    const end = Math.min(
      this.displayedCount + this.productsPerLoad,
      this.allProducts.length
    );

    const fragment = document.createDocumentFragment();

    for (let i = this.displayedCount; i < end; i++) {
      fragment.appendChild(this.allProducts[i]);
    }

    this.grid.appendChild(fragment);
    this.displayedCount = end;

    // Trigger animations if the theme uses them
    if (typeof theme !== 'undefined' && typeof theme.initAnimateOnScroll === 'function') {
      theme.initAnimateOnScroll();
    }

    // Hide sentinel if no more products
    if (this.displayedCount >= this.allProducts.length) {
      this.sentinel.style.display = 'none';
      if (this.scrollObserver) this.scrollObserver.disconnect();
    }
  }

  initInfiniteScroll() {
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.isLoading = true;
          this.loadingMore.style.display = '';

          // Small delay for visual feedback
          setTimeout(() => {
            this.displayNextBatch();
            this.loadingMore.style.display = 'none';
            this.isLoading = false;
          }, 300);
        }
      },
      { rootMargin: '200px' }
    );

    this.scrollObserver.observe(this.sentinel);
  }
}

customElements.define('collection-grid', CollectionGrid);
